import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Autocomplete, Box, Stack, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import { useDebouncedState } from "../../../../hooks/useDebouncedState";
import { formatParticipantAddress } from "../../../../utils/participantAddress";
import { ProceedingParticipantSearchResult, ProceedingParticipantType } from "../../../api/ClientFileApi";
import { useParticipantsApi } from "../../../api/ClientFileApiContext";
import { useClientFileContext } from "../../../context/ClientFileContext";
import { ClientFileQueryKey } from "../../../query/clientFileQueryKeys";

const SearchDebounceMs = 300;

interface Props {
  id?: string;
  label: string;
  type: ProceedingParticipantType;
  selectedId: string | null;
  helperText?: string;
  onChange: (participant: ProceedingParticipantSearchResult | null) => void;
}

/**
 * Search-as-you-type select over the proceeding participants of one type. The selection is transient — the
 * caller copies what it needs from the picked participant.
 */
export function ParticipantSearchSelect({ id, label, type, selectedId, helperText, onChange }: Props) {
  const { t } = useTranslation();
  const { clientId } = useClientFileContext();
  const participantsApi = useParticipantsApi();
  const {
    value: inputValue,
    debouncedValue: debouncedInputValue,
    setValue: setInputValue,
  } = useDebouncedState("", SearchDebounceMs);
  const [selectedOption, setSelectedOption] = useState<ProceedingParticipantSearchResult | null>(null);

  const { data: searchData } = useQuery({
    queryKey: [ClientFileQueryKey.ProceedingParticipants, "search", debouncedInputValue, clientId, type],
    queryFn: () =>
      participantsApi.searchParticipants({
        clientId,
        searchText: debouncedInputValue || undefined,
        types: [type],
      }),
  });
  const options = useMemo(() => searchData?.data ?? [], [searchData]);

  const value = selectedId && selectedOption?.proceedingParticipantId === selectedId ? selectedOption : null;

  return (
    <Autocomplete<ProceedingParticipantSearchResult>
      id={id}
      size="small"
      value={value}
      inputValue={inputValue}
      options={options}
      getOptionLabel={(option) => option.name}
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, val) => option.proceedingParticipantId === val.proceedingParticipantId}
      onChange={(_, newValue) => {
        setSelectedOption(newValue);
        if (!newValue) {
          setInputValue("");
        }
        onChange(newValue);
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input" || reason === "reset") {
          setInputValue(newInputValue);
        }
        if (reason === "clear") {
          setInputValue("");
          setSelectedOption(null);
          onChange(null);
        }
      }}
      renderInput={(params) => <TextField {...params} label={label} helperText={helperText} />}
      renderOption={(props, option) => {
        const address = formatParticipantAddress(option.street, option.houseNumber, option.zipCode, option.city);
        return (
          <li {...props} key={option.proceedingParticipantId}>
            <Stack>
              <Typography variant="body2">{option.name}</Typography>
              {address && (
                <Typography variant="caption" color="text.secondary">
                  {address}
                </Typography>
              )}
            </Stack>
          </li>
        );
      }}
      noOptionsText={<Box>{t("proceedingParticipants.noResults")}</Box>}
    />
  );
}
